<template>
  <div class="partner-tab-population-filter">
    <PopulationFilterOperatorRow
      label="Visibility"
      :operator="operator"
      @select="operator = $event"
    />
    <div class="partner-tab-population-filter__levels">
      <div class="font-bold text-neutral-text-strong">Sharing level</div>
      <div class="text-sm text-neutral-text-weak">
        Select sharing levels and population types.
      </div>
      <div
        class="flex flex-wrap gap-4 mt-8"
        role="group"
        aria-label="Sharing level"
      >
        <button
          v-for="level in SHARING_LEVELS_BY_SIDE[side]"
          :key="level.value"
          type="button"
          class="partner-tab-population-filter__level"
          :class="
            isSelected(level.value)
              ? SHARING_ACTIVE_CLASSES[level.value]
              : INACTIVE_CLASSES
          "
          :aria-pressed="isSelected(level.value)"
          @click="toggleLevel(level.value)"
        >
          {{ level.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import PopulationFilterOperatorRow from '@/components/reports/PopulationFilterOperatorRow.vue';

import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  SHARING_ACTIVE_CLASSES,
  type SharingType,
} from '@/constants/data_shares';

type PopulationSideType = 'partner' | 'mine';
type SharingLevelType = { label: string; value: SharingType };

const { side } = defineProps<{ side: PopulationSideType }>();

/**
 * OLAPS_ONLY holds aggregates_only, which is the Counts level, not Overlaps.
 * A partner's hidden populations never reach the client, so offering Hidden
 * on their side would match nothing.
 */
const SHARED_LEVELS: SharingLevelType[] = [
  { label: 'All', value: GREENFIELD_SHARING },
  { label: 'Overlaps', value: SHARING },
  { label: 'Counts', value: OLAPS_ONLY },
];

const SHARING_LEVELS_BY_SIDE: Record<PopulationSideType, SharingLevelType[]> = {
  partner: SHARED_LEVELS,
  mine: [...SHARED_LEVELS, { label: 'Hidden', value: HIDDEN }],
};

const INACTIVE_CLASSES =
  'border-transparent bg-neutral-background text-neutral-text hover:border-neutral-border';

const operator = ref<'OR' | 'AND'>('OR');
const selectedLevels = ref<SharingType[]>([]);

function isSelected(level: SharingType) {
  return selectedLevels.value.includes(level);
}

function toggleLevel(level: SharingType) {
  selectedLevels.value = isSelected(level)
    ? selectedLevels.value.filter((selected) => selected !== level)
    : [...selectedLevels.value, level];
}
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-population-filter__levels {
  @apply flex flex-col p-8 border-b border-neutral-border;
}

.partner-tab-population-filter__level {
  @apply px-8 py-2 rounded-full border text-sm transition-colors;

  &:focus-visible {
    @apply outline-hidden outline-2 outline-neutral-border-focus;
  }
}
</style>
